import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { NotificationPanel } from "./NotificationPanel";
import "./NotificationBell.css";
import { CircleButton } from "../common/CircleButton/circle-button";

type PendingConnection = {
  subjectListingId: string;
  objectListingId: string;
  roleType: string;
  subjectListingName: string;
  objectListingName: string;
  subjectListingImage: string | null;
  objectListingImage: string | null;
  approvalSide: "subject" | "object";
};

export const NotificationBell: React.FC = () => {
  const [pendingConnections, setPendingConnections] = useState<
    PendingConnection[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const isMobileView =
    typeof window !== "undefined" && window.innerWidth <= 800;

  const fetchPendingConnections = async () => {
    try {
      const response = await fetch("/api/pending-connections", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPendingConnections(data.pendingConnections || []);
      }
    } catch (error) {
      console.error("Error fetching pending connections:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingConnections();
  }, []);

  useEffect(() => {
    if (searchParams.get("notifications") === "open") {
      setIsOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen && !isMobileView) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobileView]);

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
      if (isMobileView) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("notifications", "open");
            return newParams;
          },
          { replace: true }
        );
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("notifications");
        return newParams;
      },
      { replace: true }
    );
  };

  const handleConnectionResponse = async (
    subjectListingId: string,
    objectListingId: string,
    roleType: string,
    action: "approve" | "reject"
  ) => {
    try {
      const response = await fetch("/api/connections/respond", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subjectListingId,
          objectListingId,
          roleType,
          action,
        }),
      });

      if (response.ok) {
        setPendingConnections((prev) =>
          prev.filter(
            (c) =>
              !(
                c.subjectListingId === subjectListingId &&
                c.objectListingId === objectListingId &&
                c.roleType === roleType
              )
          )
        );
      }
    } catch (error) {
      console.error("Error responding to connection:", error);
    }
  };

  const count = pendingConnections.length;

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <CircleButton
        onClick={handleToggle}
        badgeCount={count}
        ariaLabel={`Notifications${count > 0 ? ` (${count} pending)` : ""}`}
        iconKey="bell"
      />

      {isOpen && (
        <NotificationPanel
          pendingConnections={pendingConnections}
          loading={loading}
          onClose={handleClose}
          onRespond={handleConnectionResponse}
          isMobileView={isMobileView}
        />
      )}
    </div>
  );
};
